import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Client } from '../../types';
import ClientForm from './ClientForm';
import Footer from '../dashboard/Footer';
import './ClientDetail.css';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      setError('Unauthorized');
      setLoading(false);
      return;
    }
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const response = await api.get<Client>(`/clients/${id}`);
      setClient(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const generateReviewUrl = (): string => {
    const baseUrl = window.location.origin;
    if (!client?.uniq_id) {
      return `${baseUrl}/review/unknown`;
    }
    return `${baseUrl}/review/${client.uniq_id}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateReviewUrl());
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDelete = async () => {
    if (!client) return;
    
    try {
      setDeleting(true);
      await api.delete(`/clients/${client.id}`);
      // Navigate back to clients list after successful deletion
      navigate('/dashboard?tab=clients');
    } catch (err: any) {
      console.error('Failed to delete client:', err);
      setError(err.response?.data?.message || 'Failed to delete company');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const toggleClientStatus = async () => {
    if (!client) return;
    
    try {
      setTogglingStatus(true);
      // Handle both boolean (true/false) and integer (1/0) status values
      const statusValue = client.status as boolean | number | undefined;
      const currentStatus = statusValue !== false && statusValue !== 0;
      
      const response = await api.patch<Client>(`/clients/${client.id}/status`, {
        status: !currentStatus,
      });
      
      // Update local state immediately with the response
      setClient(response.data);
    } catch (err: any) {
      console.error('Failed to toggle client status:', err);
      setError(err.response?.data?.message || 'Failed to toggle company status');
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-container">
        <div className="loading">Loading company...</div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="detail-container">
        <div className="error-message">{error || 'Company not found'}</div>
        <button onClick={() => navigate('/dashboard?tab=clients')} className="btn btn-primary">
          Back to Companies
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="detail-container">
      <div className="detail-header">
        <button onClick={() => navigate('/dashboard?tab=clients')} className="back-button">
          ← Back to Companies
        </button>
        <h1>Company Details</h1>
      </div>

      <div className="client-detail-card">
        <div className="client-header-section">
          <div className="client-header-content">
            <div>
              <h2>{client.client_name}</h2>
              <div className="client-id">ID: {client.id}</div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowEditForm(true)}
                className="edit-button"
                title="Edit company"
              >
                ✏️ Edit
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete-button"
                title="Delete company"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>

        <div className="client-info-section">
          <div className="info-item">
            <span className="info-label">Status:</span>
            <span className="info-value">
              {(() => {
                const statusValue = client.status as boolean | number | undefined;
                const isActive = statusValue !== false && statusValue !== 0;
                return (
                  <button
                    type="button"
                    onClick={toggleClientStatus}
                    className={`status-toggle ${isActive ? 'active' : 'inactive'}`}
                    disabled={togglingStatus}
                    title={isActive ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {togglingStatus ? '...' : (isActive ? '✓ Active' : '✗ Inactive')}
                  </button>
                );
              })()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Unique ID:</span>
            <span className="info-value">{client.uniq_id || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Created:</span>
            <span className="info-value">{new Date(client.created_at).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Updated:</span>
            <span className="info-value">{new Date(client.updated_at).toLocaleString()}</span>
          </div>
        </div>

        <div className="review-url-section">
          <h3>Review URL</h3>
          <div className="review-url-container">
            <input
              type="text"
              value={generateReviewUrl()}
              readOnly
              className="review-url-input"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              type="button"
              onClick={copyToClipboard}
              className="copy-button"
              title="Copy to clipboard"
            >
              {copiedId ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
        </div>

        {client.users && client.users.length > 0 && (
          <div className="users-section">
            <h3>Associated Users</h3>
            <div className="users-list">
              {client.users.map((user) => (
                <div key={user.id} className="user-item">
                  <span className="user-name">{`${user.first_name || ''} ${user.last_name || ''}`.trim()}</span>
                  <span className="user-email">({user.email})</span>
                  <span className={`role-badge ${user.role}`}>{user.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showEditForm && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="modal-content edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <ClientForm
              editClient={client}
              onSuccess={() => {
                setShowEditForm(false);
                fetchClient(); // Refresh client data
              }}
              onCancel={() => setShowEditForm(false)}
            />
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Company</h3>
            <p>Are you sure you want to delete <strong>{client.client_name}</strong>?</p>
            <p className="warning-text">This action cannot be undone. The company will be soft-deleted.</p>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <Footer />
    </>
  );
};

export default ClientDetail;
